// DEPLOY: /var/www/woody.wtf/frontend/src/pages/FdaApprovals.js
// Latest drug approvals from the openFDA Drugs@FDA API, with sponsors matched
// to listed companies (exact or fuzzy name match) linking through to charts.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import api from '../api';

const TABLE_STYLES = `
  .fda-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .fda-table th {
    position: sticky; top: 0; z-index: 1;
    background: var(--surface);
    font-family: 'DM Mono', monospace; font-size: 10px; letter-spacing: 0.1em;
    text-transform: uppercase; color: var(--text-muted);
    text-align: left; padding: 10px 12px; border-bottom: 1px solid var(--border);
    white-space: nowrap;
  }
  .fda-table td { padding: 10px 12px; border-bottom: 1px solid var(--border); vertical-align: middle; }
  .fda-table tbody tr { transition: background 0.12s; }
  .fda-table tbody tr:hover { background: var(--accent-dim); }
  .fda-table .cell-strong { font-weight: 600; color: var(--text); }
  .fda-table .cell-sub    { font-size: 11px; color: var(--text-muted); margin-top: 2px; }
  .fda-table .cell-muted  { color: var(--text-muted); }
  .fda-table .cell-small  { font-size: 11px; color: var(--text-muted); }
  .fda-table .cell-link   { color: var(--accent); cursor: pointer; }
  .fda-table .cell-link:hover { text-decoration: underline; }
  .fda-table .cell-ticker { font-family: 'DM Mono', monospace; font-size: 11px; color: var(--text-muted); }
`;

const PAGE_STYLES = {
  page: { minHeight: '100vh', background: 'var(--bg)' },
  content: { maxWidth: '1400px', margin: '0 auto', padding: '32px' },
  header: { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '20px', gap: '16px', flexWrap: 'wrap' },
  title: { fontSize: '20px', fontWeight: 600, letterSpacing: '-0.01em' },
  sub: { fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' },
  controls: { display: 'flex', gap: '8px', alignItems: 'center' },
  tableWrap: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'calc(var(--radius) * 2)', overflow: 'auto', maxHeight: 'calc(100vh - 220px)' },
  stateBox: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'calc(var(--radius) * 2)', padding: '32px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' },
  errorBox: { background: 'var(--surface)', border: '1px solid var(--danger)', borderRadius: 'calc(var(--radius) * 2)', padding: '16px', color: 'var(--danger)', fontSize: '13px', marginBottom: '16px' },
};

const SEL = {
  background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
  color: 'var(--text)', fontSize: '12px', padding: '6px 10px',
  fontFamily: "'DM Sans', sans-serif", cursor: 'pointer', outline: 'none',
};

function sortVal(x) {
  if (x == null || x === '') return '￿';
  if (x instanceof Date) return x.getTime();
  const n = Number(x);
  return Number.isNaN(n) ? String(x).toLowerCase() : n;
}

export default function FdaApprovals() {
  const nav = useNavigate();
  const [rows, setRows]     = useState([]);
  const [count, setCount]   = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [sort, setSort]     = useState({ key: 'approval_date', dir: -1 });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get(`/fda/drug-approvals?limit=${count}`)
      .then(({ data }) => {
        if (cancelled) return;
        setRows((data?.results || []).map(r => ({ ...r, company: r.match?.name || '' })));
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err?.response?.data?.error || 'Failed to load FDA approvals.');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [count]);

  const sorted = useMemo(() => {
    const arr = [...rows];
    const { key, dir } = sort;
    arr.sort((a, b) => {
      const va = sortVal(a[key]), vb = sortVal(b[key]);
      if (va < vb) return -dir;
      if (va > vb) return dir;
      return 0;
    });
    return arr;
  }, [rows, sort]);

  function toggleSort(key) {
    setSort(s => s.key === key ? { key, dir: -s.dir } : { key, dir: 1 });
  }

  function go(path) {
    return e => { e.stopPropagation(); nav(path); };
  }

  function cell(key, r) {
    switch (key) {
      case 'approval_date':
        return r.approval_date
          ? new Date(r.approval_date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
          : '—';
      case 'drug':
        return (
          <>
            <div className="cell-strong">{r.brand_name}</div>
            {(r.dosage_form || r.route) && (
              <div className="cell-sub">{[r.dosage_form, r.route].filter(Boolean).join(' · ').toLowerCase()}</div>
            )}
          </>
        );
      case 'ingredients':
        return <span className="cell-muted">{(r.active_ingredients || []).map(i => i.toLowerCase()).join(', ') || '—'}</span>;
      case 'sponsor':
        return r.sponsor_name;
      case 'company': {
        const m = r.match;
        if (!m) return <span className="cell-muted">—</span>;
        return (
          <span className="cell-link"
                title={m.kind === 'fuzzy' ? 'approximate name match' : m.name}
                onClick={go(`/chart?ticker=${encodeURIComponent(m.symbol)}`)}>
            {m.name}
            <span className="cell-ticker"> {m.symbol}{m.kind === 'fuzzy' ? ' ~' : ''}</span>
          </span>
        );
      }
      case 'app':
        return <span className="cell-small">{r.app_type} {r.application_number}</span>;
      default:
        return r[key];
    }
  }

  const cols = [
    { key: 'approval_date', label: 'Approved' },
    { key: 'drug',          label: 'Drug' },
    { key: 'ingredients',   label: 'Active Ingredients' },
    { key: 'sponsor',       label: 'Sponsor' },
    { key: 'company',       label: 'Company / Ticker' },
    { key: 'app',           label: 'Application' },
  ];

  return (
    <div style={PAGE_STYLES.page}>
      <style>{TABLE_STYLES}</style>
      <Navbar />
      <div style={PAGE_STYLES.content}>
        <div style={PAGE_STYLES.header}>
          <div>
            <div style={PAGE_STYLES.title}>FDA Drug Approvals</div>
            <div style={PAGE_STYLES.sub}>
              Latest original approvals from Drugs@FDA. Sponsors matched to listings by name; ~ marks a fuzzy match.
            </div>
          </div>
          <div style={PAGE_STYLES.controls}>
            <select value={count} onChange={e => setCount(Number(e.target.value))} style={SEL}>
              {[25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>

        {error ? (
          <div style={PAGE_STYLES.errorBox}>{error}</div>
        ) : loading ? (
          <div style={PAGE_STYLES.stateBox}>Loading approvals…</div>
        ) : sorted.length === 0 ? (
          <div style={PAGE_STYLES.stateBox}>No approvals returned by the FDA API.</div>
        ) : (
          <div style={PAGE_STYLES.tableWrap}>
            <table className="fda-table">
              <thead>
                <tr>
                  {cols.map(c => (
                    <th key={c.key} onClick={() => toggleSort(c.key)}
                        style={{ cursor: 'pointer', opacity: sort.key === c.key ? 1 : 0.7 }}>
                      {c.label} {sort.key === c.key ? (sort.dir === 1 ? '↑' : '↓') : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((r, i) => (
                  <tr key={r.application_number + r.approval_date + i}>
                    {cols.map(c => <td key={c.key}>{cell(c.key, r)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
